import React, { useState, useRef } from 'react';
import { fetchUserAttributes, signIn, signOut } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../redux/store';
import { clearUser, setUser } from '../redux/userSlice';
import Input from './common/Input';
import Label from './common/Label';
import PasswordInput from './common/PasswordInput';
import { InternalServerError, SignInFormErrors, User } from '../types/types';
import {
  ErrorMessage,
  validateEmail,
  validatePassword,
} from '../utils/CommonUtils';
import { getUserIdByCognitoId } from '../api/UserService';
import { Link } from 'react-router-dom';
import { clearUserTypes } from '../redux/userTypesSlice';
import { clearSupports } from '../redux/supportSlice';
import { clearDepartments } from '../redux/departmentsSlice';
import { clearAllIPStatuses } from '../redux/ipStatusesSlice';
import Loading from './common/Loading';
import { clearSchools } from '../redux/schoolsSlice';
import { toast } from 'react-toastify';
import Text from './common/Text';
import Button from './common/Button';
import { APPROVED } from '../constants/Constants';

const SignIn: React.FC = () => {
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<SignInFormErrors>({});
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState<boolean | null>(null);
  const isCheckedRef = useRef(false);

  const validateFields = async () => {
    const newErrors: SignInFormErrors = {};
    if (!username) newErrors.username = 'Please enter your email address.';
    else if (!validateEmail(username))
      newErrors.username = 'Please enter a valid email address.';
    if (!password) newErrors.password = 'Please enter your password.';
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const newErrors = await validateFields();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }
    const pwdError = await validatePassword(password);
    setPasswordError(pwdError);
    if (pwdError) {
      setLoading(false);
      return;
    }
    try {
      const { isSignedIn, nextStep } = await signIn({ username, password });
      if (isSignedIn && nextStep.signInStep === 'DONE') {
        await handleUserFetch();
      }
    } catch (err: unknown) {
      handleError(err);
    }
  };

  const handleUserFetch = async () => {
    try {
      // Fetch user attributes
      const { sub, email } = await fetchUserAttributes();
      // Check if sub and email are available
      if (sub && email) {
        // Fetch user details by Cognito ID
        const result = await getUserIdByCognitoId(sub);
        if ('code' in result) {
          if (result.code === 'NOT_APPROVED') {
            setError(
              'Your account is awaiting approval from an administrator and will be accessible once approved.',
            );
          } else {
            setError(result.message);
          }
          await handleLogout();
          setLoading(false);
        } else {
          // Handle successful sign-in
          handleSuccessfulSignIn(result as User, email, sub);
          setLoading(false);
        }
      } else {
        console.log('No user found');
      }
    } catch (error) {
      // Catch any unexpected errors during the user fetch process
      console.error('Error fetching user data:', error);
      setLoading(false);
      toast.error(
        'An error occurred while fetching user details. Please try again later.',
      );
    }
  };

  const handleSuccessfulSignIn = async (
    result: User,
    email: string,
    sub: string,
  ) => {
    if (result.id) {
      if (
        result.user_status_id === APPROVED &&
        result.admin_status_id === APPROVED
      ) {
        dispatch(
          setUser({
            email,
            id: sub,
            name: `${result.first_name} ${result.last_name}`,
            userId: result.id,
            profileImageUrl: result.profile_image_url,
            userTypeId: result.user_type_id,
            schoolName: result.school_id,
            departmentName: result.department_id,
            isRememberSession: isCheckedRef.current,
          }),
        );
        navigate('/dashboard');
      } else {
        setError('Account Pending Approval');
        await handleLogout();
      }
    }
  };

  const handleError = async (err: unknown) => {
    console.error('Signin handleError error: ', err as InternalServerError);
    switch ((err as InternalServerError).message) {
      case 'Incorrect username or password.':
        setError('Your password is incorrect. Please reset your password or');
        break;
      case 'User does not exist.':
        setError(
          'An account with this email does not exist. Enter a different email or',
        );
        break;
      case 'There is already a signed in user.':
        await handleLogout();
        break;
      case 'A network error has occurred.':
        toast.error('You are offline. Please check your internet connection.');
        break;
      default:
        setError((err as InternalServerError).message || 'Error signing in');
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      dispatch(clearUser());
      dispatch(clearUserTypes());
      dispatch(clearSupports());
      dispatch(clearDepartments());
      dispatch(clearSchools());
      dispatch(clearAllIPStatuses());
      navigate('/signin');
    } catch (error) {
      console.error('Signin: Error during logout process:', error);
      toast.error('Logout failed, please try again.');
    }
  };

  return (
    <div className="common-container padding-container custom-background">
      <div className="auth-container">
        <div className="grid gap-8">
          <Text className="display-extra-small">Log in</Text>
          <Text className="medium-regular">
            Welcome back! Please enter your details.
          </Text>
        </div>
        <form
          onSubmit={handleSubmit}
          className="padding-0 grid gap-24"
          noValidate
        >
          <div className="grid gap-20">
            <div className="auth-input-group">
              <Label htmlFor="username" className="small-medium">
                Email<sup className="fs-12">*</sup>
              </Label>
              <Input
                type="email"
                className="auth-input"
                name="username"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  setPasswordError(null);
                  setError(null);
                  setErrors({});
                }}
                placeholder="Enter your email"
              />
              {errors.username && (
                <div className="error-message">
                  <p>{errors.username}</p>
                </div>
              )}
            </div>
            <div className="auth-input-group">
              <Label htmlFor="password" className="small-medium">
                Password<sup className="fs-12">*</sup>
              </Label>
              <PasswordInput
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordError(null);
                  setError(null);
                  setErrors({});
                }}
                placeholder="••••••••"
                autoComplete="off"
              />
              {errors.password && (
                <div className="error-message">
                  <p>{errors.password}</p>
                </div>
              )}
              {error && (
                <div className="error-message">
                  <Text>{<ErrorMessage message={error} />}</Text>
                </div>
              )}
              {passwordError && (
                <div className="error-message">
                  <div>{passwordError}</div>
                </div>
              )}
            </div>
          </div>
          <div className="auth-remember-forgot-container">
            <div className="auth-remember">
              <input
                type="checkbox"
                className="auth-checkbox"
                id="remember"
                onChange={(e) => {
                  isCheckedRef.current = e.target.checked;
                }}
              />
              <Label htmlFor="remember" className="small-medium">
                Remember for 30 days
              </Label>
            </div>
            <div className="auth-forgot-password">
              <Link to="/forgot-password" className="small-semi-bold">
                Forgot password
              </Link>
            </div>
          </div>
          <div className="d-flex justify-content-center">
            <Button
              type="submit"
              className="tertiary-button"
              disabled={loading != null && loading}
            >
              {loading ? (
                <>
                  <Loading size={25} /> &nbsp; Sign in
                </>
              ) : (
                'Sign in'
              )}
            </Button>
          </div>
        </form>
        <div className="align-center">
          <Text className="small-regular">
            Don’t have an account?
            <Link to="/request-invite" className="small-semi-bold">
              &nbsp;Request an Invitation
            </Link>
          </Text>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
